import { ReactElement, useMemo } from 'react';
import { Routes, Route } from 'react-router-dom';

import { ROUTE_VIEW_MAPPING, INSTANCE_PATH_ELEMENT_MAP, InjectableViewsAdapterProps } from '@localstack/resources';

import { buildRoute, getSelectedInstance, useEndpoint } from '@localstack/services';

import { PATH_PREFIX_INSTANCE } from '@localstack/constants';

import { AppRoute } from '~/config';

import { InstanceHandler, ResourceBrowserHandler } from './Adapters';

export const InjectedInstanceRoutes = (): ReactElement => {
  const selectedInstance = getSelectedInstance();
  const { endpoint: globalEndpoint } = useEndpoint();
  const instanceEndpoint = selectedInstance?.endpoint || globalEndpoint;

  const instancePathPrefix = `${PATH_PREFIX_INSTANCE}/`;

  // Expand Handler chain here but make
  // sure the order is correct!
  const LayoutHandlerChain = new ResourceBrowserHandler();
  LayoutHandlerChain.setNext(new InstanceHandler());

  const instanceRoutes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(AppRoute as Record<string, string>).filter((v) => v[1].startsWith(instancePathPrefix)),
      ),
    [],
  );

  const instanceBuiltRoutes = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(instanceRoutes).map((v) => [v[0], buildRoute(v[1], { iid: selectedInstance?.id })]),
      ),
    [selectedInstance],
  );

  const mergedRouteViewMapping = useMemo(() => {
    const formattedInstanceMap = new Map(
      Object.entries(INSTANCE_PATH_ELEMENT_MAP).map(([path, mapEntry]) => [path, Object.values(mapEntry).at(0)]),
    );
    return new Map(Array.from(formattedInstanceMap).concat(Array.from(ROUTE_VIEW_MAPPING)));
  }, []);

  const InstanceRoutes = useMemo(
    () => (
      <Routes>
        {Array.from(mergedRouteViewMapping.keys()).map((route) => {
          const Adapter = LayoutHandlerChain.handle(route);
          return (
            <Route
              key={route}
              // since our customer routes specifies a general route for all resources
              // we need to strip away the prefix, since it's already included in the general route
              path={route.replace(instancePathPrefix, '')}
              element={
                <Adapter
                  Component={mergedRouteViewMapping.get(route) as InjectableViewsAdapterProps['Component']}
                  routes={instanceBuiltRoutes}
                  endpoint={instanceEndpoint}
                />
              }
            />
          );
        })}
      </Routes>
    ),
    [mergedRouteViewMapping, instanceEndpoint, instanceBuiltRoutes],
  );

  return InstanceRoutes;
};
